import s from './highlight.module.scss';

interface HighlightTextProps {
  text: string;
  searchText?: string;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

type MatchRange = { start: number; end: number };

/** Сжать пробелы; для каждой буквы сжатой строки — индекс в исходной. */
function mapWithoutSpaces(value: string): { compact: string; indexMap: number[] } {
  let compact = '';
  const indexMap: number[] = [];
  for (let i = 0; i < value.length; i += 1) {
    if (/\s/.test(value[i])) continue;
    indexMap.push(i);
    compact += value[i];
  }
  return { compact, indexMap };
}

/** Диапазоны совпадений: сначала точные, иначе — запрос без пробелов в тексте без пробелов. */
function findMatchRanges(text: string, query: string): MatchRange[] {
  const exact = new RegExp(escapeRegExp(query), 'gi');
  const ranges: MatchRange[] = [];
  let match = exact.exec(text);
  while (match) {
    if (!match[0]) break;
    ranges.push({ start: match.index, end: match.index + match[0].length });
    match = exact.exec(text);
  }
  if (ranges.length) return ranges;

  // Запрос без пробелов тоже ищем в тексте без пробелов («газна» → «ГАЗ НА»).
  const queryCompact = query.replace(/\s+/g, '');
  if (!queryCompact) return ranges;

  const { compact, indexMap } = mapWithoutSpaces(text);
  const loose = new RegExp(escapeRegExp(queryCompact), 'gi');
  let looseMatch = loose.exec(compact);
  while (looseMatch) {
    if (!looseMatch[0]) break;
    const from = looseMatch.index;
    const to = from + looseMatch[0].length - 1;
    ranges.push({ start: indexMap[from], end: indexMap[to] + 1 });
    looseMatch = loose.exec(compact);
  }
  return ranges;
}

/** Есть ли в тексте то, что подсветил бы `HighlightText`, — по тем же правилам. */
export const hasSearchMatch = (text: string | null | undefined, searchText?: string) => {
  const query = searchText?.trim();
  return Boolean(query && text && findMatchRanges(text, query).length);
};

/** Все вхождения запроса без учёта регистра; при необходимости — без пробелов (как поиск на бэке). */
export const HighlightText = ({ text, searchText }: HighlightTextProps) => {
  const query = searchText?.trim();
  if (!query) return <span>{text}</span>;

  const ranges = findMatchRanges(text, query);
  if (!ranges.length) return <span>{text}</span>;

  const nodes = [];
  let lastIndex = 0;
  ranges
    .slice()
    .sort((a, b) => a.start - b.start)
    .forEach((range, i) => {
      if (range.start < lastIndex) return;
      if (range.start > lastIndex) {
        nodes.push(
          <span key={`text-${lastIndex}`}>{text.slice(lastIndex, range.start)}</span>,
        );
      }
      nodes.push(
        <span key={`match-${range.start}-${i}`} className={s.highlight}>
          {text.slice(range.start, range.end)}
        </span>,
      );
      lastIndex = range.end;
    });

  if (lastIndex < text.length) {
    nodes.push(<span key={`text-${lastIndex}`}>{text.slice(lastIndex)}</span>);
  }

  return <span>{nodes}</span>;
};
